/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import isNil from 'lodash/isNil';
import Terminal from '../shared/entities/terminal';
import TerminalHistory from '../shared/entities/terminalHistory';
import { OrbitPoint, SpaceVehiclePosition } from '../shared/entities/haloNet';
import { SEC_TO_MS_CONVERSION } from './DateTimeUtils';
import { feetToMiles } from './DistanceUtils';
import { getSpaceVehiclePositionAtTime, MS_TO_SECONDS } from './HaloNetUtils';
import { knotToMphNumber } from './VelocityUtils';

/**
 * Default for haloNetPositionFallbackAfterSeconds, used before the portal config has loaded.
 *
 * Four telemetry polls at the default apiPollingIntervalSeconds of 15. A space vehicle in low
 * earth orbit covers about 450 km in that time, so the bound is deliberately tight.
 */
export const DEFAULT_HALONET_POSITION_FALLBACK_AFTER_SECONDS = 60;

interface OverlayCacheEntry {
  sourceTerminal: Terminal;
  position: SpaceVehiclePosition | undefined;
  result: Terminal;
}

const overlayCache: Map<string, OverlayCacheEntry> = new Map();

/**
 * True when the terminal reports its own position and that report is recent enough to trust.
 *
 * A terminal with no sourceTime is treated as stale, because there is no way to show that the
 * position is current. A terminal with no latitude or longitude has nothing to prefer.
 *
 * No feed writes a position for a space vehicle today, so these fields are usually absent and
 * the propagated position is what gets used.
 */
export const hasFreshTelemetryPosition = (
  terminal: Terminal,
  fallbackAfterSeconds: number = DEFAULT_HALONET_POSITION_FALLBACK_AFTER_SECONDS,
  nowMs: number = Date.now(),
): boolean => {
  if (isNil(terminal.latitudeDeg) || isNil(terminal.longitudeDeg) || isNil(terminal.sourceTime)) {
    return false;
  }

  const ageSeconds = nowMs * MS_TO_SECONDS - terminal.sourceTime;

  return ageSeconds <= fallbackAfterSeconds;
};

/**
 * True when the user is not allowed to see where this terminal is.
 *
 * atlantis-service hides a location by nulling latitudeDeg, longitudeDeg and altitudeFeet, which
 * is the same state as a terminal that simply never reported one. Without this test the HaloNet
 * position would be shown in place of the location that was deliberately withheld.
 */
export const isPositionWithheld = (terminal: Terminal): boolean =>
  !!terminal.restrictionEnabled || !!terminal.aprBlankingEnabled;

/**
 * Give a space vehicle terminal the position that the map and the details panel should show.
 *
 * The position the terminal reports for itself wins, because it is a measurement rather than a
 * model. The HaloNet propagated position is used when the terminal reports no position, or when
 * the position it reports is older than fallbackAfterSeconds, which comes from the portal config.
 *
 * A terminal whose location is withheld keeps no position at all.
 *
 * sourceTime is deliberately left alone. It dates the whole telemetry record, and the rest of the
 * portal reads it as the time the terminal was last heard from, for the connection state, the last
 * reported banner and how long a tail tag stays on the map. The time of the propagated position
 * goes on positionSourceTime instead.
 *
 * The result keeps the same object identity while the terminal and the position do not change.
 * Components that subscribe with shallowEqual compare these references, so a new object on every
 * call makes them render again for every dispatched action. The freshness test runs before the
 * cache is read, so the choice of source is never served from the cache.
 */
export const overlaySpaceVehiclePosition = (
  terminal: Terminal,
  position: SpaceVehiclePosition | undefined,
  fallbackAfterSeconds: number = DEFAULT_HALONET_POSITION_FALLBACK_AFTER_SECONDS,
  nowMs: number = Date.now(),
): Terminal => {
  if (!position || isPositionWithheld(terminal) || hasFreshTelemetryPosition(terminal, fallbackAfterSeconds, nowMs)) {
    return terminal;
  }

  const cached = overlayCache.get(terminal.datapathUuid);
  if (cached && cached.sourceTerminal === terminal && cached.position === position) {
    return cached.result;
  }

  const result: Terminal = {
    ...terminal,
    latitudeDeg: position.latitudeDeg,
    longitudeDeg: position.longitudeDeg,
    altitudeFeet: position.altitudeFeet,
    positionSourceTime: position.sourceTime,
    isPositionOutsidePropagationWindow: position.isOutsidePropagationWindow,
  };

  overlayCache.set(terminal.datapathUuid, { sourceTerminal: terminal, position, result });

  return result;
};

// Exposed for tests. The cache is keyed by datapathUuid and holds one entry per space vehicle.
export const clearSpaceVehiclePositionOverlayCache = () => {
  overlayCache.clear();
};

export interface SpaceVehicleChartHistory extends TerminalHistory {
  altitudeMiles?: number | null;
  velocityMph?: number | null;
}

export type SpaceVehicleChartHistoryByTimestamp = { [key: number]: SpaceVehicleChartHistory };

interface ChartHistoryCacheEntry {
  orbitPoints: OrbitPoint[] | undefined;
  isWithheld: boolean;
  fallbackAfterSeconds: number;
  result: SpaceVehicleChartHistoryByTimestamp;
}

// Keyed by the history object, so a history that the store has replaced can be garbage collected.
const chartHistoryCache: WeakMap<object, ChartHistoryCacheEntry> = new WeakMap();

// One decimal place for miles and two for mph, as the details panel shows them.
const feetToChartMiles = (feet: number): number => Number.parseFloat(feetToMiles(feet).toFixed(1));

// A null value is kept, because the chart draws a gap for null and skips a missing value.
const convertValue = (value: number | null | undefined, convert: (value: number) => number) =>
  isNil(value) ? value : convert(value);

/**
 * Give each point of a space vehicle terminal history the altitude in miles and the speed in mph,
 * from the same sources as the details panel.
 *
 * The altitude the terminal reports wins. A point with no reported altitude takes the HaloNet
 * propagated altitude for its time, but only when the last reported altitude is older than
 * fallbackAfterSeconds, as in overlaySpaceVehiclePosition. The history type has no latitude or
 * longitude. atlantis-forwarder drops a position report that has no altitude, so a reported altitude
 * shows that the terminal reported its position.
 *
 * HaloNet propagates forward from the time that halonet-service last filled its cache, so older
 * points have no propagated altitude. They get none, because the clamped end point that
 * getSpaceVehiclePositionAtTime gives would draw a false flat line. A point that atlantis-service
 * APR blanked gets none, and neither does any point of a terminal whose location is withheld.
 *
 * Speed is never propagated, as in the details panel.
 *
 * The result keeps the same object identity while its inputs do not change. The chart compares
 * histories by reference, so a new object on every call makes it render again for every action.
 */
export const toSpaceVehicleChartHistory = (
  history: { [key: number]: TerminalHistory },
  orbitPoints: OrbitPoint[] | undefined,
  isWithheld: boolean,
  fallbackAfterSeconds: number = DEFAULT_HALONET_POSITION_FALLBACK_AFTER_SECONDS,
): SpaceVehicleChartHistoryByTimestamp => {
  const cached = chartHistoryCache.get(history);
  if (
    cached &&
    cached.orbitPoints === orbitPoints &&
    cached.isWithheld === isWithheld &&
    cached.fallbackAfterSeconds === fallbackAfterSeconds
  ) {
    return cached.result;
  }

  const canUsePropagatedAltitude = !isWithheld && !!orbitPoints && orbitPoints.length > 0;
  let lastReportedAltitudeTime: number | undefined;

  const result = Object.entries(history)
    .sort(([, a], [, b]) => a.sourceTime - b.sourceTime)
    .reduce((chartHistory: SpaceVehicleChartHistoryByTimestamp, [timestamp, entry]: [string, TerminalHistory]) => {
      let altitudeMiles = convertValue(entry.altitudeFeet, feetToChartMiles);

      if (!isNil(entry.altitudeFeet)) {
        lastReportedAltitudeTime = entry.sourceTime;
      } else if (
        canUsePropagatedAltitude &&
        !entry.aprBlankingEnabled &&
        (isNil(lastReportedAltitudeTime) || entry.sourceTime - lastReportedAltitudeTime > fallbackAfterSeconds)
      ) {
        const position = getSpaceVehiclePositionAtTime(
          orbitPoints as OrbitPoint[],
          new Date(entry.sourceTime * SEC_TO_MS_CONVERSION),
        );
        if (position && !position.isOutsidePropagationWindow) {
          altitudeMiles = feetToChartMiles(position.altitudeFeet);
        }
      }

      chartHistory[Number(timestamp)] = {
        ...entry,
        altitudeMiles,
        velocityMph: convertValue(entry.velocityKnots, knotToMphNumber),
      };

      return chartHistory;
    }, {});

  chartHistoryCache.set(history, { orbitPoints, isWithheld, fallbackAfterSeconds, result });

  return result;
};
